import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import { capitalize, displayCurrency, handleChangeEventInput, unsavedChangesExist } from "helpers/Helpers";
import { IAccommodationReservation, IAccommodationReservationNote, IFolioLineItem, IFolioTransaction } from "./types";
import { faEllipsisVertical, faExternalLink, faPlus } from "@fortawesome/pro-regular-svg-icons";
import {
  GetCustomerPaymentMethod,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "components/badge/Badge";
import { ButtonNew as Button } from "components/buttonNew";
import "./editReservation.scss";
import DataTable from "components/dataTable/DataTable";
import moment from "moment";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { ICreditCard } from "pages/guest/Customer/CustomerProfile";
import CardSection from "components/cardSection/CardSection";
import { CreditCardItem } from "components/creditCardItem/CreditCardItem";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js/types/stripe-js/elements";
import Spin from "components/spin/spin";
import Products, { IProductState } from "elements/register/Products";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IAccommodationUnitType } from "../unitTypes/UnitTypes";
import {
  DeleteAccommodationReservationNote,
  GetAccommodationReservations,
  PostAccommodationReservationNote,
  PutAccommodationReservation,
  PutAccommodationReservationNote,
} from "api/rpc/2024-04/facilityAdmin/accommodation/reservations";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import _ from "lodash";
import Icon from "components/icon/Icon";
import Popup from "components/popup/Popup";
import { PostLineItemsToOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import TextField from "components/form/textField/TextField";
import Tabs from "components/tabs/Tabs";
import { ReservationNote } from "components/accommodation/reservationNote/ReservationNote";

const validProvinceCodes = ["AB", "BC", "MB", "NB", "NL", "NT", "NS", "NU", "ON", "PE", "QC", "SK", "YT"];
const TABLE_LIMIT = 20;
const validStateCodes = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];

const isValidPostalCode = (code: string) => /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(code);
const isValidProvinceCode = (code: string) =>
  validProvinceCodes.includes(code.toUpperCase()) || validStateCodes.includes(code.toUpperCase());

type FolioItem = (IFolioLineItem & { itemType: "line_item" }) | (IFolioTransaction & { itemType: "transaction" });

const tabs = [
  {
    id: "chargesCredits",
    content: "Charges / Credits",
  },
  {
    id: "notes",
    content: "Notes",
  },
];

export function EditReservation() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { reservationId }: any = useParams();
  const { facilityStore } = useAppSelector(store => store);
  const stripe = useStripe();
  const elements = useElements();

  const [editReservation, setEditReservation] = useState<IAccommodationReservation>({
    id: null,
    facility_id: undefined,
    unit_type_id: undefined,
    customer_id: undefined,
    customer: null,
    arrival_date: new Date(),
    departure_date: new Date(),
    nights: undefined,
    currency: "",
    quoted_total_amount: undefined,
    source: null,
    status: "",
    adults: undefined,
    children: undefined,
    notes: [],
  });

  const [filterState, setFilterState] = useState({
    makePaymentActive: false,
    payFullBalance: false,
    partialPayment: false,
    paymentAmount: "",
    paymentSheetStep: 1, // 1: amount selection, 2: payment method selection, 3: add new card
    selectedPaymentMethod: null,
    addProductActive: false,
    addProductSearchQuery: "",
    addProductLoadingMessage: "",
    addProductResult: [] as IProduct[],
    productState: {
      selectedProduct: undefined,
      variants: [],
      variantSheetOpen: false,
    } as IProductState,
    addProductWarning: false,
    selectedVariant: undefined,
    tableOffset: 0,
    viewNotes: false,
  });

  const [editReservationBeforeChanges, setEditReservationBeforeChanges] =
    useState<IAccommodationReservation>(undefined);
  const [accommodationUnitType, setAccommodationUnitType] = useState<IAccommodationUnitType>(undefined);
  const [customerPaymentMethods, setCustomerPaymentMethods] = useState<ICreditCard[]>([]);
  const [foundProducts, setFoundProducts] = useState<IProduct[]>(filterState.addProductResult);

  const [newCreditCardState, setNewCreditCardState] = useState({
    newCreditCardCustomerName: "",
    newCreditCardZipPostalCode: "",
    newCreditCardStateProvince: "",
    elements: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    saveCard: false,
  });

  const [folioNoteState, setFolioNoteState] = useState({
    newNote: "",
    folioNoteModalOpen: false,
    folioNotes: [] as string[],
    deleteNoteModalOpen: false,
    noteToDelete: null,
    noteToEdit: null,
  });

  const [selected, setSelected] = useState<number>(0);

  const sortedFolioItems = useMemo(() => {
    if (!editReservation?.folio) {
      return [];
    }

    const lineItems: FolioItem[] = (editReservation.folio.line_items || []).map(item => ({
      ...item,
      itemType: "line_item" as const,
    }));

    const transactions: FolioItem[] = (editReservation.folio.transactions || []).map(item => ({
      ...item,
      itemType: "transaction" as const,
    }));

    const combined = [...lineItems, ...transactions];

    return combined.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [editReservation?.folio]);

  // Calculate the folio totals
  const folioTotals = useMemo(() => {
    if (!editReservation?.folio) {
      return {
        taxLines: [] as Array<{ name: string; total: number; rate: number }>,
        subtotal: 0,
        balance: 0,
        currency: "",
      };
    }

    const subtotal = editReservation.folio.subtotal_price || 0;
    const taxLineMap: Record<string, { total: number; rate: number }> = {};

    (editReservation.folio.line_items || []).forEach(item => {
      (item.tax_lines || []).forEach(t => {
        const title = t.title || "Tax";
        const price = Number(t.price) || 0;
        const rate = Number(t.rate) || 0;

        if (!taxLineMap[title]) {
          taxLineMap[title] = { total: 0, rate };
        }
        taxLineMap[title].total += price;
      });
    });

    const taxLines = Object.entries(taxLineMap).map(([name, { total, rate }]) => ({
      name,
      total,
      rate,
    }));

    const credits = (editReservation.folio.transactions || []).reduce(
      (sum, transaction) => sum + (transaction.amount || 0),
      0,
    );

    const balance = (editReservation.folio.total_price || 0) - credits;

    return {
      taxLines,
      credits,
      subtotal,
      balance,
      currency: editReservation?.currency,
    };
  }, [editReservation?.folio]);

  function formatDateMDY(dateStr: string) {
    if (!dateStr) {
      return "";
    }

    const [year, month, day] = dateStr.substring(0, 10).split("-");

    return `${month}/${day}/${year}`;
  }

  useEffect(() => {
    void loadAccommodationReservation();
  }, []);

  useEffect(() => {
    if (filterState.addProductActive && filterState.addProductResult.length === 0) {
      void loadAllProducts();
    }
  }, [filterState.addProductActive]);

  async function loadAccommodationReservation() {
    const accommodationReservationRes = await GetAccommodationReservations(
      { id: reservationId, with_folio: true, with_notes: true },
      false,
    );

    if (accommodationReservationRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading reservations"));
      return;
    }

    void loadCustomerPaymentMethods(Number(accommodationReservationRes?.data?.[0]?.customer_id));
    void loadAccommodationUnitTypes(Number(accommodationReservationRes?.data?.[0]?.unit_type_id));

    setEditReservation(accommodationReservationRes?.data?.[0] as IAccommodationReservation);
    setEditReservationBeforeChanges(accommodationReservationRes?.data?.[0] as IAccommodationReservation);
  }

  async function loadAccommodationUnitTypes(unit_type_id: number) {
    if (accommodationUnitType !== undefined) {
      setAccommodationUnitType(undefined);
    }
    const accommodationUnitTypesRes = await GetAccommodationUnitTypes({ id: unit_type_id }, true);

    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading unit types"));

      setAccommodationUnitType(undefined);
      return;
    }

    setAccommodationUnitType(accommodationUnitTypesRes?.data?.[0] as IAccommodationUnitType);
  }

  async function loadCustomerPaymentMethods(customer_id: number) {
    const res = await GetCustomerPaymentMethod({ customer_id: customer_id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error loading customers"));
      return;
    }

    setCustomerPaymentMethods(res.data as ICreditCard[]);
  }

  function handleCardInputChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setNewCreditCardState(prevState => ({
      ...prevState,
      elements: {
        ...newCreditCardState?.elements,
        [e.elementType]: e.complete,
      },
    }));
  }

  function handleCloseMakePayment() {
    setFilterState(prevState => ({
      ...prevState,
      makePaymentActive: false,
      paymentSheetStep: 1,
      payFullBalance: false,
      partialPayment: false,
      paymentAmount: "",
      selectedPaymentMethod: null,
    }));

    setNewCreditCardState({
      newCreditCardCustomerName: "",
      newCreditCardZipPostalCode: "",
      newCreditCardStateProvince: "",
      elements: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
      },
      saveCard: false,
    });
  }

  async function handleReservationCheckIn() {
    const resp = await PutAccommodationReservation({ id: reservationId, status: "checked-in" }, false);
    if (resp.status !== StatusCode.OK) {
      return;
    }

    const reservation = { ...editReservation, status: "checked-in" };
    setEditReservationBeforeChanges(reservation);
    setEditReservation(reservation);
  }

  async function handleReservationCheckOut() {
    const resp = await PutAccommodationReservation({ id: reservationId, status: "checked-out" }, false);
    if (resp.status !== StatusCode.OK) {
      return;
    }

    const reservation = { ...editReservation, status: "checked-out" };
    setEditReservationBeforeChanges(reservation);
    setEditReservation(reservation);
  }

  async function loadAllProducts() {
    setFilterState(prevState => ({
      ...prevState,
      addProductLoadingMessage: "Loading products...",
    }));

    const productsRes = await GetProduct({ extended: true }, false);

    if (productsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading products")));
      setFilterState(prevState => ({
        ...prevState,
        addProductLoadingMessage: "",
      }));
      return;
    }

    setFilterState(prevState => ({
      ...prevState,
      addProductResult: productsRes.data,
      addProductLoadingMessage: "",
    }));
  }

  function filterProducts(event: any) {
    const keyword = String(event.target.value);

    if (keyword !== "") {
      const results = filterState.addProductResult.filter(item => {
        return item.title.toLowerCase().includes(keyword.toLowerCase());
      });
      setFoundProducts(results);
    } else {
      setFoundProducts([]);
    }

    setFilterState(prevState => ({
      ...prevState,
      addProductSearchQuery: keyword,
    }));
  }

  function alphaSortProducts(products: IProduct[]) {
    const productsCopy: IProduct[] = [...products];

    productsCopy.sort((firstProd: IProduct, secondProd: IProduct) => {
      if (firstProd?.title < secondProd?.title) {
        return -1;
      } else if (firstProd?.title > secondProd?.title) {
        return 1;
      } else {
        return 0;
      }
    });

    return productsCopy;
  }

  function onClickProduct(product: IProduct) {
    if (product.variant_count > 1) {
      setFilterState(prevState => ({
        ...prevState,
        productState: {
          selectedProduct: product,
          variants: product.variants,
          variantSheetOpen: true,
        },
      }));
    } else {
      onConfirmVariant(product.variants[0]);
    }
  }

  function onCancelAddProductModal() {
    setFilterState(prevState => ({
      ...prevState,
      addProductActive: false,
      addProductSearchQuery: "",
      productState: {
        selectedProduct: undefined,
        variants: [],
        variantSheetOpen: false,
      },
    }));
    setFoundProducts([]);
  }

  function onConfirmVariant(variant: IVariant) {
    setFilterState(prevState => ({
      ...prevState,
      selectedVariant: variant,
      addProductWarning: true,
    }));

    onCancelAddProductModal();
  }

  function handleCloseAddProductWarning() {
    setFilterState(prevState => ({
      ...prevState,
      addProductWarning: false,
      selectedVariant: undefined,
    }));
  }

  async function handleAddProduct() {
    const params = {
      variant_id: filterState.selectedVariant?.id,
      quantity: 1,
      order_id: editReservation?.folio?.id,
    };

    const addProductRes = await PostLineItemsToOrder(params, true);

    if (addProductRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding product to reservation")));
      void handleCloseAddProductWarning();
      return;
    }

    dispatch(showSuccess(t("Product added to reservation")));
    void handleCloseAddProductWarning();
    void loadAccommodationReservation();
  }

  async function handleAddNewCreditCard() {
    let newPaymentMethod: ICreditCard = null;
    const customer = editReservation?.customer;
    try {
      if (!stripe || !elements) {
        return;
      }

      const cardElement = elements.getElement("cardNumber");
      if (!cardElement) {
        return;
      }

      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: customer?.full_name,
          email: customer?.email,
          phone: customer?.phone,
          address: {
            state: customer?.state,
          },
        },
      });
      if (stripeError) {
        dispatch(showError(stripeError.message));
        return;
      } else {
        const paymentMethodRes = await setUpCard(paymentMethod?.id);

        if (paymentMethodRes === null) {
          console.log("Card set up error");
          return;
        }

        newPaymentMethod = paymentMethodRes;
      }
    } catch (e) {
      console.log(e);
    }

    void handleMakePayment(newPaymentMethod);
  }

  async function setUpCard(payment_method_id: string) {
    try {
      // Create card setup intent
      const setupRes = await PostSetup({ customer_id: editReservation?.customer?.id }, true);
      if (setupRes.status !== StatusCode.OK) {
        dispatch(showError(String(setupRes?.data?.message)));
        return null;
      }
      //Confim card setup intent
      const stripeConfirmRes = await stripe?.confirmCardSetup(String(setupRes?.data?.setup_intent?.client_secret), {
        payment_method: payment_method_id,
      });
      if (stripeConfirmRes?.error) {
        dispatch(showError(stripeConfirmRes?.error?.message));
        return null;
      } else {
        //Post payment method
        const paymentMethodRes = await PostPaymentMethod(
          {
            payment_method_id: stripeConfirmRes?.setupIntent?.payment_method,
            customer_id: editReservation?.customer?.id,
            save_card: true,
          },
          true,
        );

        if (paymentMethodRes?.status !== StatusCode.OK) {
          dispatch(showError(String(paymentMethodRes?.data?.message)));
          return null;
        } else {
          dispatch(showSuccess("Successfully added card"));
          return paymentMethodRes?.data as ICreditCard;
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function handleMakePayment(paymentMethod?: ICreditCard) {
    const manualCardPaymentOption = facilityStore?.paymentOptions?.find(
      paymentOption => paymentOption.payment_method === "manual_card",
    );

    const postTransactionRes = await PostTransaction(
      {
        kind: "sale",
        amount: Number(filterState.paymentAmount),
        order_id: editReservation?.folio?.id,
        payment_option_id: manualCardPaymentOption?.id,
      },
      true,
    );

    if (postTransactionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating transaction")));
      return;
    }

    if (
      postTransactionRes.data.client_secret &&
      (paymentMethod?.stripe_payment_method_id || filterState.selectedPaymentMethod?.stripe_payment_method_id) &&
      postTransactionRes.data.id &&
      editReservation?.folio?.id
    ) {
      if (!stripe) {
        console.error("Stripe hook error");
        return;
      }
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        postTransactionRes.data.client_secret,
        {
          payment_method: paymentMethod
            ? paymentMethod?.stripe_payment_method_id
            : filterState.selectedPaymentMethod?.stripe_payment_method_id,
        },
      );

      if (stripeError) {
        dispatch(showError(stripeError.message));
        return;
      }

      const captureRes = await PutCapture(
        { payment_intent_id: paymentIntent.id, transaction_id: postTransactionRes.data.id },
        true,
      );

      if (captureRes.status !== StatusCode.OK) {
        dispatch(showError(String(t("Error capturing payment"))));
        return;
      }
    } else {
      dispatch(showError(String(t("Error creating card payment"))));

      return;
    }

    dispatch(showSuccess(t("Payment made successfully")));
    setFilterState(prevState => ({
      ...prevState,
      makePaymentActive: false,
      paymentSheetStep: 1,
      payFullBalance: false,
      partialPayment: false,
      paymentAmount: "",
      selectedPaymentMethod: null,
    }));

    void loadAccommodationReservation();
  }

  const reservationRatePlans = Array.from(new Set(editReservation?.nightly_rates?.map(r => r.rate_plan.code)));

  const handleOpenAddNoteModal = () => {
    setFolioNoteState(prevState => ({
      ...prevState,
      folioNoteModalOpen: true,
      noteToEdit: null,
    }));
  };

  const handleCloseAddNoteModal = () => {
    setFolioNoteState(prevState => ({
      ...prevState,
      folioNoteModalOpen: false,
      newNote: "",
      noteToEdit: null,
    }));
  };

  const handleCreateNote = async () => {
    const params = {
      id: editReservation?.id,
      note: folioNoteState.newNote,
    };

    const res = await PostAccommodationReservationNote(params, false);

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating note")));
      return;
    }

    setFolioNoteState(prevState => ({
      ...prevState,
      folioNoteModalOpen: false,
      newNote: "",
      noteToEdit: null,
    }));

    void loadAccommodationReservation();
  };

  const handleDeleteNote = async (note: IAccommodationReservationNote) => {
    const res = await DeleteAccommodationReservationNote({ id: editReservation.id, note_id: note.id }, false);

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting note")));
      return;
    }

    setFolioNoteState(prevState => ({
      ...prevState,
      deleteNoteModalOpen: false,
      noteToDelete: null,
    }));

    void loadAccommodationReservation();
  };

  const handleEditNote = async () => {
    const res = await PutAccommodationReservationNote(
      {
        id: editReservation.id,
        note_id: folioNoteState.noteToEdit.id,
        note: folioNoteState.noteToEdit.content,
      },
      false,
    );

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error editing note")));
      return;
    }
    setFolioNoteState(prevState => ({
      ...prevState,
      folioNoteModalOpen: false,
    }));

    void loadAccommodationReservation();
  };

  const handleNoteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (folioNoteState.noteToEdit) {
      setFolioNoteState(prevState => ({
        ...prevState,
        noteToEdit: { ...prevState.noteToEdit, content: e.target.value },
      }));
    } else {
      setFolioNoteState(prevState => ({
        ...prevState,
        newNote: e.target.value,
      }));
    }
  };

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  return (
    <Page
      breadcrumbs={[
        { label: t("Accommodations"), url: "/admin/accommodation/calendar" },
        { label: t("Reservations"), url: "/admin/accommodation/reservations" },
        {
          label: t("Reservation #" + editReservation?.id),
          url: "/admin/accommodation/reservations/" + String(reservationId),
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(editReservation, editReservationBeforeChanges),
        onAction: () => console.log("Update reservation"),
        onCancel: () => setEditReservation(editReservationBeforeChanges),
      }}
    >
      <Card>
        <Card.Section>
          <div className="edit-reservation-guest-container">
            <div>
              <div className="flex">
                <p className="edit-reservation-guest-container_customer_name">{editReservation?.customer?.full_name}</p>
                <AccommodationReservationStatusBadge reservation={editReservation} />
              </div>

              <p className="edit-reservation-guest-container_reservation_number">{"Res. #" + editReservation?.id}</p>
            </div>

            <div>
              <NavigationDropdownNew
                rightAlign
                hideChevron
                label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                type="primary"
                sections={[
                  [
                    {
                      label: t("Check-out"),
                      onClick: handleReservationCheckOut,
                      icon: "plane-departure",
                    },
                    {
                      label: t("Check-in"),
                      onClick: handleReservationCheckIn,
                      icon: "plane-arrival",
                    },
                  ],
                  [
                    {
                      label: t("Add a note"),
                      onClick: handleOpenAddNoteModal,
                      icon: "file",
                    },
                  ],
                ]}
              />
            </div>
          </div>
          <hr />

          <div className="edit-reservation-details-container">
            <div className="edit-reservation-details-container_overview_container">
              <div>
                <p className="edit-reservation-overview-label">{"Check-in"}</p>
                <p className="edit-reservation-overview-value">
                  {formatDateMDY(editReservation?.arrival_date.toString())}
                </p>
              </div>
              <div>
                <p className="edit-reservation-overview-label">{"Check-out"}</p>
                <p className="edit-reservation-overview-value">
                  {formatDateMDY(editReservation?.departure_date.toString())}
                </p>
              </div>
              <div>
                <p className="edit-reservation-overview-label">{"Nights"}</p>
                <p className="edit-reservation-overview-value">{editReservation?.nights}</p>
              </div>
              <div>
                <p className="edit-reservation-overview-label">{"Unit type"}</p>
                <p className="edit-reservation-overview-value">{accommodationUnitType?.title}</p>
              </div>
              <div>
                <p className="edit-reservation-overview-label">{"Rate Plan(s)"}</p>
                <p className="edit-reservation-overview-value">{reservationRatePlans?.join(", ")}</p>
              </div>
            </div>

            <div className="edit-reservation-details-container_balance_container">
              <p className="edit-reservation-details-label">{"Balance due"}</p>
              <p className="edit-reservation-details-value">
                {folioTotals.balance.toFixed(2)} {editReservation?.currency?.toUpperCase()}
              </p>
            </div>
          </div>
        </Card.Section>
      </Card>

      <div className="mt-3 mb-3 flex overflow-x-auto">
        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
      </div>

      {selected === 0 && (
        <Card>
          <Card.Section>
            <div className="flex justify-between mb-4">
              <div>
                <p className="edit-reservation-table-title">{t("Charges / credits")}</p>
                <p className="edit-reservation-table-subtitle">
                  {t("All charges, payments, and adjustments for this reservation.")}
                </p>
              </div>

              <div className="flex gap-10">
                <NavigationDropdownNew
                  rightAlign
                  hideChevron
                  label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                  type="primary"
                  sections={[
                    [
                      // {
                      //   label: t("Print Invoice"),
                      //   onClick: () => console.log("Print Invoice"),
                      //   icon: "print",
                      // },
                      {
                        label: t("Make payment"),
                        onClick: () => setFilterState(prevState => ({ ...prevState, makePaymentActive: true })),
                        icon: "money-bill",
                      },
                      {
                        label: t("Add a product"),
                        onClick: () => setFilterState(prevState => ({ ...prevState, addProductActive: true })),
                        icon: "tag",
                      },
                    ],
                  ]}
                />
              </div>
            </div>

            <DataTable
              columns={[
                { label: t("Date") },
                { label: t("Description") },
                { label: t("Debit") },
                { label: t("Credit"), width: "18%" },
              ]}
              loading={editReservation?.folio === undefined}
              footer={{
                tableLimit: TABLE_LIMIT,
                tableOffset: filterState.tableOffset,
                handleTableOffset: direction =>
                  setFilterState(prev => ({
                    ...prev,
                    tableOffset: direction === "prev" ? prev.tableOffset - TABLE_LIMIT : prev.tableOffset + TABLE_LIMIT,
                  })),
                disableNextOffset: !sortedFolioItems || sortedFolioItems?.length <= TABLE_LIMIT,
              }}
              footerTotals={folioTotals}
            >
              {sortedFolioItems.map(item => {
                if (item.itemType === "line_item") {
                  const clickable = item.source_order_id !== null;

                  const taxTitles =
                    item.tax_lines && item.tax_lines.length > 0
                      ? ` (${item.tax_lines.map(tax => tax.title).join(", ")})`
                      : "";

                  return (
                    <tr key={`line_item_${item.id}`} className={clickable ? "clickable" : ""}>
                      <td>{item.created_at ? moment(item.created_at, "YYYY-MM-DD").format("LL") : ""}</td>
                      {item.source_order_id == null ? (
                        <td>
                          {item.variant_title}
                          <span className="text-semibold">{taxTitles}</span>
                        </td>
                      ) : (
                        <td>
                          <a href={"/admin/order/" + item.source_order_id} target="_blank" rel="noreferrer">
                            <div className="flex gap-2 text-sm font-medium text-gray-600">
                              {item.variant_title}
                              {taxTitles}
                              {item.order_id != null && (
                                <div>
                                  <Icon icon={faExternalLink} />
                                </div>
                              )}
                            </div>
                          </a>
                        </td>
                      )}
                      <td>
                        {displayCurrency(editReservation?.currency ? editReservation?.currency : "CAD", item.price)}
                      </td>
                      <td></td>
                    </tr>
                  );
                } else {
                  return (
                    <tr key={`transaction_${item.id}`}>
                      <td>{item.created_at ? moment(item.created_at, "YYYY-MM-DD").format("LL") : ""}</td>
                      <td>
                        <div className="flex flex-col">
                          <span>
                            {capitalize(item.kind)} - {capitalize(item.payment_type)} - •••• {item.payment_last_4}
                          </span>
                        </div>
                      </td>
                      <td></td>
                      <td>
                        -{displayCurrency(editReservation?.currency ? editReservation?.currency : "CAD", item.amount)}
                      </td>
                    </tr>
                  );
                }
              })}
            </DataTable>
          </Card.Section>
        </Card>
      )}

      {selected === 1 && (
        <Card title={t("Notes")} subtitle={t("Manage customer and facility notes here")}>
          <hr />
          <Card.Section>
            <div className="edit-reservation-notes-container">
              {editReservation.notes?.map(note => (
                <ReservationNote
                  key={note.id}
                  note={note}
                  onEdit={note =>
                    setFolioNoteState(prevState => ({ ...prevState, folioNoteModalOpen: true, noteToEdit: note }))
                  }
                  onDelete={note =>
                    setFolioNoteState(prevState => ({
                      ...prevState,
                      deleteNoteModalOpen: true,
                      noteToDelete: note,
                    }))
                  }
                />
              ))}
            </div>

            <Button
              type="text"
              size="medium"
              onClick={() =>
                setFolioNoteState(prevState => ({ ...prevState, folioNoteModalOpen: true, noteToEdit: null }))
              }
              style={{ padding: "15px 0px 0px 0px" }}
              className="flex gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              {t("Add new note")}
            </Button>
          </Card.Section>
        </Card>
      )}

      <Portal isMounted={filterState?.makePaymentActive}>
        <Sheet
          open={filterState?.makePaymentActive}
          title={
            filterState.paymentSheetStep === 1
              ? t("Make a Payment")
              : filterState.paymentSheetStep === 2
              ? t("Select Payment Method")
              : t("Add New Card")
          }
          supportText={
            filterState.paymentSheetStep === 1
              ? "Perform a transaction towards " + editReservation?.customer?.full_name + "'s folio."
              : filterState.paymentSheetStep === 2
              ? "Choose a payment method or add a new card."
              : "Enter the new card details."
          }
          size="small"
          onCancel={() => {
            if (filterState.paymentSheetStep === 1) {
              // Cancel from step 1 - close the sheet
              setFilterState(prevState => ({
                ...prevState,
                makePaymentActive: false,
                paymentSheetStep: 1,
                payFullBalance: false,
                partialPayment: false,
                paymentAmount: "",
                selectedPaymentMethod: null,
              }));
            } else {
              // Go back from step 2 or 3
              setFilterState(prevState => ({
                ...prevState,
                paymentSheetStep: prevState.paymentSheetStep - 1,
              }));
            }
          }}
          onClose={() => {
            handleCloseMakePayment();
          }}
          cancelText={filterState.paymentSheetStep === 1 ? t("Cancel") : t("Go back")}
          backDropCancel
          closable
          okText={filterState.paymentSheetStep === 1 ? t("Continue") : t("Confirm & pay")}
          onOk={() => {
            if (filterState.paymentSheetStep === 1) {
              setFilterState(prevState => ({ ...prevState, paymentSheetStep: 2 }));
            } else if (filterState?.paymentSheetStep === 2) {
              handleMakePayment().catch(err => console.error("Payment failed", err));
            } else {
              handleAddNewCreditCard().catch(err => console.error("Card add failed", err));
            }
          }}
          okDisabled={
            filterState.paymentSheetStep === 1
              ? !filterState?.payFullBalance && (!filterState?.partialPayment || !filterState?.paymentAmount)
              : filterState.paymentSheetStep === 2
              ? !filterState?.selectedPaymentMethod
              : !newCreditCardState.newCreditCardCustomerName ||
                !newCreditCardState.elements.cardNumber ||
                !newCreditCardState.elements.cardExpiry ||
                !newCreditCardState.elements.cardCvc ||
                !isValidPostalCode(newCreditCardState.newCreditCardZipPostalCode) ||
                !isValidProvinceCode(newCreditCardState.newCreditCardStateProvince)
          }
          overflow
        >
          <div>
            {/* Step 1: Payment Amount Selection */}
            {filterState.paymentSheetStep === 1 && (
              <FormLayout>
                <FormLayout.Group>
                  <div className="flex flex-column gap-4">
                    <p className="edit-reservation-step-title">{t("How much would you like to pay?")}</p>

                    <Checkbox
                      id="payFullBalance"
                      size="medium"
                      value={filterState?.payFullBalance}
                      checked={filterState?.payFullBalance}
                      onChange={() =>
                        setFilterState({
                          ...filterState,
                          payFullBalance: true,
                          partialPayment: false,
                          paymentAmount: editReservation?.folio?.total_price.toString(),
                        })
                      }
                      label={
                        t("Pay full balance") +
                        " (" +
                        displayCurrency(
                          editReservation?.currency ? editReservation?.currency : "CAD",
                          editReservation?.folio?.total_price,
                        ) +
                        ")"
                      }
                      circle
                    />

                    <Checkbox
                      id="partialPayment"
                      size="medium"
                      value={filterState?.partialPayment}
                      checked={filterState?.partialPayment}
                      onChange={() => setFilterState({ ...filterState, payFullBalance: false, partialPayment: true })}
                      label={t("Partially pay")}
                      circle
                    />
                  </div>
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input
                    label={t("Paid amount")}
                    id={"paymentAmount"}
                    value={filterState?.paymentAmount}
                    onChange={e => setFilterState(prevState => ({ ...prevState, paymentAmount: e.target.value }))}
                    placeholder="0"
                    type={"number"}
                    prefix={"$"}
                    disabled={!filterState?.partialPayment}
                  />
                </FormLayout.Group>
              </FormLayout>
            )}

            {/* Step 2: Payment Method Selection */}
            {filterState.paymentSheetStep === 2 && (
              <FormLayout>
                <FormLayout.Group>
                  <div>
                    <p className="edit-reservation-step-title mb-2">{t("Select a credit card:")}</p>

                    {customerPaymentMethods?.map(method => (
                      <CreditCardItem
                        key={method.id}
                        creditCard={method}
                        isSelected={filterState?.selectedPaymentMethod?.id === method?.id}
                        onClick={card =>
                          setFilterState(prevState => ({
                            ...prevState,
                            selectedPaymentMethod: prevState.selectedPaymentMethod?.id === card.id ? null : card,
                          }))
                        }
                        showCheckbox
                        alignTop
                        moreOptions
                      />
                    ))}
                  </div>
                </FormLayout.Group>
                <FormLayout.Group>
                  <Button
                    onClick={() =>
                      setFilterState(prevState => ({ ...prevState, paymentSheetStep: 3, selectedPaymentMethod: null }))
                    }
                    type="text"
                    size="small"
                    className="flex items-center gap-2 pl-0"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    {t("Add new payment method")}
                  </Button>
                </FormLayout.Group>
              </FormLayout>
            )}

            {/* Step 3: Add New Card */}
            {filterState.paymentSheetStep === 3 && (
              <FormLayout>
                <FormLayout.Group>
                  <div>
                    <Input
                      id="newCreditCardCustomerName"
                      value={newCreditCardState?.newCreditCardCustomerName}
                      label={t("Name on card")}
                      onChange={e => handleChangeEventInput(e, newCreditCardState, setNewCreditCardState)}
                      placeholder={t("Enter full name of cardholder")}
                    />

                    <div className="mt-2">
                      <CardSection type="two-row" onChange={handleCardInputChange} />
                    </div>
                  </div>
                </FormLayout.Group>

                <FormLayout.Group>
                  <hr />
                </FormLayout.Group>

                <FormLayout.Group>
                  <Input
                    id="newCreditCardStateProvince"
                    value={newCreditCardState?.newCreditCardStateProvince}
                    label={t("State / Province")}
                    onChange={e => handleChangeEventInput(e, newCreditCardState, setNewCreditCardState)}
                    placeholder={t("ON")}
                    error={
                      newCreditCardState?.newCreditCardStateProvince &&
                      newCreditCardState.newCreditCardStateProvince !== ""
                        ? !isValidProvinceCode(newCreditCardState.newCreditCardStateProvince)
                        : false
                    }
                    maxLength={2}
                  />
                  <Input
                    id="newCreditCardZipPostalCode"
                    value={newCreditCardState?.newCreditCardZipPostalCode}
                    label={t("Postcode")}
                    onChange={e => handleChangeEventInput(e, newCreditCardState, setNewCreditCardState)}
                    placeholder={t("A1A 1A1")}
                    error={
                      newCreditCardState?.newCreditCardZipPostalCode &&
                      newCreditCardState.newCreditCardZipPostalCode !== ""
                        ? !isValidPostalCode(newCreditCardState.newCreditCardZipPostalCode)
                        : false
                    }
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  <div className="mt-2">
                    <Checkbox
                      id={"save_card"}
                      size="small"
                      value={newCreditCardState?.saveCard}
                      checked={newCreditCardState?.saveCard}
                      onChange={() =>
                        setNewCreditCardState({ ...newCreditCardState, saveCard: !newCreditCardState?.saveCard })
                      }
                      label={t("Save card for future bookings")}
                    />
                  </div>
                </FormLayout.Group>
              </FormLayout>
            )}
          </div>
        </Sheet>
      </Portal>

      <Portal>
        <Sheet
          open={filterState.addProductActive}
          size="medium"
          closable
          backDropCancel={false}
          title={t("Add product")}
          onCancel={onCancelAddProductModal}
          cancelText={t("Cancel")}
          okText={t("Add")}
        >
          {filterState?.addProductLoadingMessage ? (
            <div style={{ display: "flex", height: "100%", margin: "auto" }}>
              <div className="text-center m-auto">
                <div style={{ height: "32px" }}>
                  <Spin />
                </div>
                <div>{filterState?.addProductLoadingMessage}</div>
              </div>
            </div>
          ) : (
            <>
              <Input
                type="search"
                onChange={filterProducts}
                placeholder={t("Search products...")}
                value={filterState.addProductSearchQuery}
                autoComplete="off"
              />
              <div className="overflow-auto mt-4">
                <Products
                  products={alphaSortProducts(
                    foundProducts.length > 0
                      ? foundProducts
                      : filterState.addProductResult.length > 0
                      ? filterState.addProductResult
                      : [],
                  )}
                  useGridPositions={false}
                  productState={filterState.productState}
                  onClick={onClickProduct}
                  onCancel={onCancelAddProductModal}
                  onConfirmVariant={onConfirmVariant}
                  currency={facilityStore?.facility?.default_currency_code}
                />
              </div>
            </>
          )}
        </Sheet>
      </Portal>

      <Portal isMounted={filterState.addProductWarning}>
        <Popup
          okText={t("Confirm")}
          onCancel={() => handleCloseAddProductWarning()}
          onOk={handleAddProduct}
          title={t("Add product")}
          type="warning"
          description={t(
            "Are you sure you want to add" + " " + filterState.selectedVariant?.title + " " + "to this reservation?",
          )}
          open={filterState.addProductWarning}
        />
      </Portal>

      <Portal isMounted={folioNoteState.folioNoteModalOpen}>
        <Sheet
          open={folioNoteState.folioNoteModalOpen}
          size="small"
          onCancel={handleCloseAddNoteModal}
          onOk={() => (folioNoteState.noteToEdit ? handleEditNote() : handleCreateNote())}
          title={folioNoteState.noteToEdit ? "Edit note" : "Create new note"}
          okText={folioNoteState.noteToEdit ? "Save" : "Create"}
        >
          <div className="mt-4 mb-4">
            <TextField
              label={t("Note")}
              value={folioNoteState.noteToEdit ? folioNoteState.noteToEdit.content : folioNoteState.newNote}
              onChange={handleNoteChange}
              rows={4}
            />
          </div>
        </Sheet>
      </Portal>

      <Portal isMounted={folioNoteState.deleteNoteModalOpen}>
        <Popup
          type="warning"
          open={folioNoteState.deleteNoteModalOpen}
          title={t("Delete Note?")}
          description={t("Are you sure you want to delete this note?")}
          closable
          onCancel={() =>
            setFolioNoteState(prevState => ({ ...prevState, deleteNoteModalOpen: false, noteToDelete: null }))
          }
          onOk={() => handleDeleteNote(folioNoteState.noteToDelete as IAccommodationReservationNote)}
          cancelText={t("Cancel")}
          okText={t("Delete")}
        />
      </Portal>
    </Page>
  );
}

export function AccommodationReservationStatusBadge({ reservation }: { reservation: IAccommodationReservation }) {
  return (
    <Badge
      type={
        reservation.status === "checked-in" ? "success" : reservation.status === "checked-out" ? "error" : "warning"
      }
      size="small"
    >
      {_.startCase(reservation.status)}
    </Badge>
  );
}
